import React, { useEffect } from 'react';
import { Subscription } from 'rxjs';
import { ChildReference, FieldConfigDTO, FormMode, GuslErrors } from 'src/components/types';
import { log } from 'src/services/LogService';
import { arrayIsEmpty, isDefined, notDefined } from './TypeCheckers';

export const assignReferences = (
  reference: ChildReference,
  onFormModeChange: (mode: FormMode) => void,
  onRefresh: () => void,
  doValidation: () => boolean,
) => {
  reference.changeMode = onFormModeChange;
  reference.doRefresh = onRefresh;
  reference.doValidation = doValidation;
  reference.register(reference);
};

export const getCssValue = (cssVariable: string): string => {
  const docStyle = getComputedStyle(document.documentElement);
  if (docStyle) {
    return docStyle.getPropertyValue(cssVariable);
  }
  return '';
};

export const canColorise = (fieldConfig: FieldConfigDTO): boolean => {
  if (notDefined(fieldConfig)) {
    return false;
  }
  try {
    if (!fieldConfig.properties) return false;
    const properties = JSON.parse(fieldConfig.properties);
    return properties?.colorise || false;
  } catch {
    return false;
  }
};

export const canColoriseText = (fieldConfig: FieldConfigDTO): boolean => {
  if (notDefined(fieldConfig)) {
    return false;
  }
  try {
    if (!fieldConfig.properties) return false;
    const properties = JSON.parse(fieldConfig.properties);
    return properties?.coloriseText || false;
  } catch {
    return false;
  }
};

export const noop = () => {};

export const getErrorMessage = (error: unknown): string => {
  if (
    !error ||
    typeof error !== 'object' ||
    !('data' in error) ||
    notDefined((error as { data?: unknown }).data)
  ) {
    return 'Server Error';
  }

  const guslErrors = (error as { data: GuslErrors }).data;

  if (notDefined(guslErrors)) {
    return 'Server Error';
  }
  if (arrayIsEmpty(guslErrors?.errors)) {
    return 'Server Error';
  }
  let errorMessage = guslErrors.errors[0].message || 'Server Error';

  for (let x = 0; x < 5; x++) {
    if (errorMessage.indexOf('{' + x + '}') >= 0) {
      if (guslErrors.errors[0].parameters.length > x) {
        errorMessage = errorMessage.replace('{0}', guslErrors.errors[0].parameters[0]);
      } else {
        return errorMessage;
      }
    } else {
      return errorMessage;
    }
  }
  return errorMessage;
};

export const getErrorMessageKey = (error: unknown): string => {
  if (
    !error ||
    typeof error !== 'object' ||
    !('data' in error) ||
    notDefined((error as { data?: unknown }).data)
  ) {
    return 'server.error';
  }

  const guslErrors = (error as { data: GuslErrors }).data;

  if (notDefined(guslErrors)) {
    return 'server.error';
  }
  if (arrayIsEmpty(guslErrors?.errors)) {
    return 'server.error';
  }
  let messageKey = guslErrors.errors[0].messageKey || 'server.error';

  for (let x = 0; x < 5; x++) {
    if (messageKey.indexOf('{' + x + '}') >= 0) {
      if (guslErrors.errors[0].parameters.length > x) {
        messageKey = messageKey.replace('{0}', guslErrors.errors[0].messageKey);
      } else {
        return messageKey;
      }
    } else {
      return messageKey;
    }
  }
  return messageKey;
};

export interface IMountedMonitor {
  getValue: () => boolean;
  isMounted: (logging?: boolean) => boolean;
}
export const useMountedMonitor = (className?: string, logging?: boolean): IMountedMonitor => {
  const mounted = React.useRef(false);

  useEffect(() => {
    mounted.current = true;
    if (className && logging) log.info(className, 'MON001', 'Mounted');

    return () => {
      mounted.current = false;
      if (className && logging) log.info(className, 'MON002', 'Unmounted');
    };
  }, []);

  return {
    getValue: () => mounted.current,
    isMounted: (withLog?: boolean) => {
      if (withLog) log.info(className || '', 'MON003', 'Mounted:', mounted.current);
      return mounted.current;
    },
  };
};

export const MountedMonitor = (className?: string, logging?: boolean): IMountedMonitor => {
  let mounted = false;
  useEffect(() => {
    mounted = true;
    if (className && logging) {
      log.info(className, 'MON001', 'Mounted');
    }
    return () => {
      mounted = false;
      if (className && logging) {
        log.info(className, 'MON002', 'Unmounted');
      }
    };
  }, []);
  return {
    getValue: () => {
      return mounted;
    },
    isMounted: (logging?: boolean) => {
      if (logging) {
        log.info(className || '', 'MON003', 'Mounted:', mounted);
      }
      return mounted;
    },
  };
};

export const unSubscribe = (subscription: Subscription) => {
  try {
    if (isDefined(subscription)) {
      subscription.unsubscribe();
    }
  } catch (error) {
    log.error('unSubscribe', 'ERR001', 'Unsubscribed failed', error);
  }
};

export const cancelAbortController = (abortController: AbortController) => {
  try {
    if (isDefined(abortController)) {
      abortController.abort();
    }
  } catch (error) {
    log.error('cancelAbortController', 'ERR001', 'Abort failed', error);
  }
};

/*
    At let the Voodoo commence
 */
export const parseFunction = (func: string) => {
  const regEx = /function *\(([^()]*)\)[ \n\t]*{(.*)}/gim;
  const match = regEx.exec(func.replace(/\n/g, ' '));

  if (match) {
    // new Function params ..., body
    // "function (a, b) { return a + b; }"

    return new Function(...match[1].split(','), match[2]);
  }

  return null;
};

export const constructUrl = (baseUrl: string, data: Record<string, unknown>): string => {
  let url = baseUrl;
  Object.keys(data)
    .filter((k) => !!data[k])
    .forEach((k) => {
      url = url.replace(`{${k}}`, String(data[k]));
    });
  return url;
};
export const capitaliseFirstChar = (string: string) => {
  try {
    return string[0].toUpperCase() + string.slice(1).toLowerCase();
  } catch {
    return string;
  }
};

export function sortObjectByValuesOrKeys(
  obj: { [key: string]: number },
  order: 'asc' | 'desc' = 'asc',
  sortBy: 'key' | 'value' = 'value',
): { [key: string]: number } {
  const entries = Object.entries(obj);

  if (sortBy === 'key') {
    if (order === 'asc') {
      entries.sort((a, b) => a[0].localeCompare(b[0]));
    } else if (order === 'desc') {
      entries.sort((a, b) => b[0].localeCompare(a[0]));
    } else {
      throw new Error("Invalid order parameter. Use 'asc' or 'desc'.");
    }
  } else if (sortBy === 'value') {
    if (order === 'asc') {
      entries.sort((a, b) => a[1] - b[1]);
    } else if (order === 'desc') {
      entries.sort((a, b) => b[1] - a[1]);
    } else {
      throw new Error("Invalid order parameter. Use 'asc' or 'desc'.");
    }
  } else {
    throw new Error("Invalid sortBy parameter. Use 'key' or 'value'.");
  }

  return Object.fromEntries(entries);
}
export function rgbToHex(rgb: string): string {
  const [r, g, b] = rgb.split(',').map((v) => parseInt(v.trim(), 10));

  const toHex = (n: number) => n.toString(16).padStart(2, '0');

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}
